/**
 * Iter 153 — Smart Post Editor
 *
 * Replaces the plain ItemDialog on the Content Calendar.
 * Features:
 *   • Tabs: Details | Preview | Linked Tasks | Hashtags | AI time
 *   • Realistic per-channel previews (IG, LinkedIn, Facebook, X, YouTube, WhatsApp)
 *   • Linked-task inline CRUD (existing task pick + new task inline creation)
 *   • Hashtag suggestions — static instant, upgrade with Claude AI
 *   • AI publish-time button with candidate list + reason
 *   • Time input HH:MM (15-min accuracy handled by browser input step=900)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Trash2, ListChecks, Instagram, Facebook, Linkedin, Twitter, Youtube, MessageCircle, Mail, Globe, Clock, Wand2, Plus, X, Hash, Heart, MessageSquare, Repeat, Send } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export const CHANNEL_ICONS = {
  instagram: Instagram, facebook: Facebook, linkedin: Linkedin,
  twitter: Twitter, youtube: Youtube, whatsapp: MessageCircle, email: Mail, other: Globe,
};
export const CHANNEL_LABELS = {
  instagram: "Instagram", facebook: "Facebook", linkedin: "LinkedIn",
  twitter: "X (Twitter)", youtube: "YouTube", whatsapp: "WhatsApp", email: "Email", other: "Other",
};
import SmartRichText from "@/components/SmartRichText";

// Iter 153 polish — per-channel caption limits (source: official platform limits, Feb 2026).
// Warn threshold triggers a yellow badge at 80%; over-limit shows a red badge.
export const CHANNEL_LIMITS = {
  instagram: 2200, facebook: 63206, linkedin: 3000, twitter: 280,
  youtube: 5000, whatsapp: 1024, email: 100000, other: 5000,
};

function _charBadge(count, limit) {
  const pct = count / limit;
  if (count > limit) {
    return <span className="chip chip-rejected text-[9px] font-mono" title={`Over by ${count - limit}`}>{count}/{limit}</span>;
  }
  if (pct >= 0.8) {
    return <span className="chip chip-warning text-[9px] font-mono">{count}/{limit}</span>;
  }
  return <span className="chip text-[9px] font-mono text-muted-foreground">{count}/{limit}</span>;
}

const ITEM_STATUSES = ["planned", "briefed", "in_production", "internal_qc", "client_review", "approved", "scheduled", "published", "cancelled"];
const STATUS_LABEL = (s) => (s || "").replaceAll("_", " ");

export default function SmartPostEditor({ item, onClose, onChanged, initialTab }) {
  const [form, setForm] = useState({
    status: item.status,
    planned_date: item.planned_date || "",
    planned_time: item.planned_time || "",
    topic: item.topic || "",
    pillar: item.pillar || "",
    notes: item.notes || "",
    caption: item.caption || "",
    // Nov 2026 · Onscreen content — the actual on-screen copy / headline
    // that appears in the creative, distinct from the post `caption`.
    onscreen_content: item.onscreen_content || "",
    channels: item.channels || [item.category || "instagram"],
    hashtags: item.hashtags || [],
    primary_assignee_user_id: item.primary_assignee_user_id || "",
  });
  const [tasks, setTasks] = useState(null);
  const [brand, setBrand] = useState(null);
  const [pillars, setPillars] = useState([]);
  // R268www · Feb 2026 — Workspace members for the primary-assignee
  // picker rendered under Details. Fetched once on open so PMs can
  // override a single deliverable's owner without touching the
  // package template.
  const [wsMembers, setWsMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [t, b, m] = await Promise.all([
        api.get(`/content-items/${item.id}/tasks`),
        api.get(`/brands/${item.brand_id}`),
        api.get(`/workspaces/${item.workspace_id}/members`).catch(() => ({ data: [] })),
      ]);
      setTasks(t.data || []);
      setBrand(b.data);
      setPillars((b.data.pillars || []).map((p) => p.name).filter(Boolean));
      setWsMembers(m.data || []);
    } catch { setTasks([]); }
  }, [item.id, item.brand_id, item.workspace_id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/content-items/${item.id}`, {
        ...form,
        pillar: form.pillar || null,
        topic: form.topic || null,
        notes: form.notes || null,
        caption: form.caption || null,
        onscreen_content: form.onscreen_content || null,
        planned_time: form.planned_time || null,
        primary_assignee_user_id: form.primary_assignee_user_id || null,
      });
      toast.success("Saved");
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${item.name}"? Open spawned tasks will also be removed.`)) return;
    try {
      await api.delete(`/content-items/${item.id}`);
      toast.success("Deleted");
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="smart-post-editor">
        <DialogHeader>
          <DialogTitle className="text-base">{item.name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">
          {item.brand_name} · {item.package_name || item.category} · {item.category}
        </div>

        <Tabs defaultValue={initialTab || "details"} className="w-full mt-2">
          <TabsList className="rounded-none flex-wrap h-auto justify-start">
            <TabsTrigger value="details" className="rounded-none" data-testid="spe-tab-details">Details</TabsTrigger>
            <TabsTrigger value="preview" className="rounded-none" data-testid="spe-tab-preview">Preview</TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-none" data-testid="spe-tab-tasks">Linked tasks{tasks && tasks.length ? ` · ${tasks.length}` : ""}</TabsTrigger>
            <TabsTrigger value="hashtags" className="rounded-none" data-testid="spe-tab-hashtags">Hashtags</TabsTrigger>
            <TabsTrigger value="ai-time" className="rounded-none" data-testid="spe-tab-ai-time">AI time</TabsTrigger>
            <TabsTrigger value="timeline" className="rounded-none" data-testid="spe-tab-timeline">Activity + Comments</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="pt-4">
            <DetailsPane form={form} set={set} pillars={pillars} wsMembers={wsMembers} />
          </TabsContent>

          <TabsContent value="preview" className="pt-4">
            <PreviewPane form={form} brand={brand} item={item} />
          </TabsContent>

          <TabsContent value="tasks" className="pt-4">
            <TasksPane item={item} tasks={tasks} onReloaded={async () => { await load(); await onChanged?.(); }} />
          </TabsContent>

          <TabsContent value="hashtags" className="pt-4">
            <HashtagsPane itemId={item.id} form={form} set={set} />
          </TabsContent>

          <TabsContent value="ai-time" className="pt-4">
            <AiTimePane itemId={item.id} form={form} set={set} />
          </TabsContent>

          <TabsContent value="timeline" className="pt-4">
            <TimelinePane wsId={item.workspace_id} itemId={item.id} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="destructive" className="rounded-none mr-auto" onClick={del} data-testid="spe-delete">
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="spe-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Details pane ───────────

function DetailsPane({ form, set, pillars, wsMembers = [] }) {
  const allChans = Object.keys(CHANNEL_LABELS);
  const toggleChan = (c) => {
    if (form.channels.includes(c)) set("channels", form.channels.filter((x) => x !== c));
    else set("channels", [...form.channels, c]);
  };
  return (
    <div className="space-y-3" data-testid="spe-details">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Status</Label>
          <Select value={form.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="h-9 rounded-none" data-testid="spe-status"><SelectValue /></SelectTrigger>
            <SelectContent>{ITEM_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL(s)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Planned date</Label>
          <Input type="date" value={form.planned_date}
                 onChange={(e) => set("planned_date", e.target.value)}
                 className="h-9 rounded-none" data-testid="spe-date" />
        </div>
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Planned time (24h)</Label>
          <Input type="time" step="900" value={form.planned_time}
                 onChange={(e) => set("planned_time", e.target.value)}
                 className="h-9 rounded-none" data-testid="spe-time" />
        </div>
        {/* R268www · Feb 2026 — Primary assignee (deliverable owner)
            override. Defaults to the package-line owner at generation;
            editing here scopes the change to this single content item. */}
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Primary assignee</Label>
          <select
            value={form.primary_assignee_user_id || ""}
            onChange={(e) => set("primary_assignee_user_id", e.target.value)}
            className="h-9 w-full rounded-none border border-input bg-background px-2 text-sm"
            data-testid="spe-primary-assignee"
          >
            <option value="">— none —</option>
            {[...(wsMembers || [])]
              .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""))
              .map((m) => (
                <option key={m.user_id || m.id} value={m.user_id || m.id}>
                  {m.name || m.email}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div>
        <Label className="label-eyebrow mb-1 inline-block">Channels</Label>
        <div className="flex flex-wrap gap-1.5">
          {allChans.map((c) => {
            const Icon = CHANNEL_ICONS[c] || Globe;
            const on = form.channels.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleChan(c)}
                className={`text-xs px-2 py-1 border flex items-center gap-1 ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                data-testid={`spe-channel-${c}`}
              >
                <Icon className="w-3 h-3" /> {CHANNEL_LABELS[c]}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="label-eyebrow mb-1 inline-block">Topic / working title</Label>
        <Input value={form.topic} onChange={(e) => set("topic", e.target.value)}
               className="h-9 rounded-none" data-testid="spe-topic" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Content pillar</Label>
          <Select value={form.pillar || "_none"} onValueChange={(v) => set("pillar", v === "_none" ? "" : v)}>
            <SelectTrigger className="h-9 rounded-none" data-testid="spe-pillar"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">None</SelectItem>
              {pillars.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="label-eyebrow mb-1 inline-block">Onscreen content</Label>
        <SmartRichText
          value={form.onscreen_content}
          onChange={(v) => set("onscreen_content", v)}
          placeholder="Headline / overlay text that appears IN the creative…"
          minHeight={80}
          data-testid="spe-onscreen"
        />
      </div>

      <div>
        <Label className="label-eyebrow mb-1 inline-block">Caption</Label>
        <SmartRichText
          value={form.caption}
          onChange={(v) => set("caption", v)}
          placeholder="Write the post copy…"
          minHeight={120}
          data-testid="spe-caption"
        />
      </div>

      <div>
        <Label className="label-eyebrow mb-1 inline-block">Internal notes</Label>
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  rows={2} className="rounded-none" data-testid="spe-notes" />
      </div>
    </div>
  );
}

// ─────────── Preview pane — realistic per-channel mockups ───────────

function PreviewPane({ form, brand, item }) {
  if (!brand) return <div className="text-sm text-muted-foreground">Loading brand…</div>;
  const captionText = form.caption || item.topic || item.name || "";
  const tagText = (form.hashtags || []).join(" ");
  const brandHandle = brand.marketing_channels?.find((c) => c.platform === form.channels[0])?.handle || `@${(brand.name || "brand").toLowerCase().replace(/\s+/g, "")}`;
  const followerCount = brand.marketing_channels?.find((c) => c.platform === form.channels[0])?.followers_manual || 1234;
  const mediaHint = brand.logo_url || null;
  return (
    <div className="space-y-4" data-testid="spe-preview">
      {form.channels.length === 0 && (
        <div className="text-xs italic text-muted-foreground">Select at least one channel in Details to see previews.</div>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {form.channels.map((c) => {
          if (c === "instagram") return <InstagramPreview key={c} brand={brand} handle={brandHandle} caption={captionText} tags={tagText} media={mediaHint} />;
          if (c === "linkedin") return <LinkedInPreview key={c} brand={brand} handle={brandHandle} followers={followerCount} caption={captionText} tags={tagText} media={mediaHint} />;
          if (c === "facebook") return <FacebookPreview key={c} brand={brand} handle={brandHandle} caption={captionText} tags={tagText} media={mediaHint} />;
          if (c === "twitter") return <TwitterPreview key={c} brand={brand} handle={brandHandle} caption={captionText} tags={tagText} />;
          if (c === "youtube") return <YoutubePreview key={c} brand={brand} handle={brandHandle} caption={captionText} tags={tagText} media={mediaHint} />;
          if (c === "whatsapp") return <WhatsAppPreview key={c} brand={brand} caption={captionText} tags={tagText} media={mediaHint} />;
          return <GenericPreview key={c} channel={c} caption={captionText} tags={tagText} />;
        })}
      </div>
    </div>
  );
}

function _Avatar({ src, fallback }) {
  return (
    <div className="w-9 h-9 bg-muted flex items-center justify-center overflow-hidden shrink-0 rounded-full">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold">{fallback}</span>}
    </div>
  );
}

function InstagramPreview({ brand, handle, caption, tags, media }) {
  const initial = (brand.name || "B").slice(0, 2).toUpperCase();
  const total = (caption?.length || 0) + (tags?.length || 0) + (tags ? 1 : 0);
  return (
    <div className="border border-border" data-testid="spe-preview-instagram">
      <div className="flex items-center gap-2 p-2 border-b border-border">
        <_Avatar src={media} fallback={initial} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{handle.replace(/^@/, "")}</div>
          <div className="text-[10px] text-muted-foreground">Sponsored</div>
        </div>
        {_charBadge(total, CHANNEL_LIMITS.instagram)}
        <Instagram className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="aspect-square bg-gradient-to-br from-pink-400 via-purple-500 to-orange-400 flex items-center justify-center">
        {media ? <img src={media} alt="" className="w-full h-full object-cover" /> : <span className="text-white text-4xl font-bold">{initial}</span>}
      </div>
      <div className="p-2 space-y-1">
        <div className="flex gap-3">
          <Heart className="w-5 h-5" />
          <MessageSquare className="w-5 h-5" />
          <Send className="w-5 h-5" />
        </div>
        <div className="text-xs whitespace-pre-wrap line-clamp-4"><b>{handle.replace(/^@/, "")}</b> {caption}</div>
        {tags && <div className="text-[11px] text-sky-600 whitespace-pre-wrap line-clamp-2">{tags}</div>}
      </div>
    </div>
  );
}

function LinkedInPreview({ brand, handle, followers, caption, tags, media }) {
  const initial = (brand.name || "B").slice(0, 2).toUpperCase();
  const total = (caption?.length || 0) + (tags?.length || 0) + (tags ? 1 : 0);
  return (
    <div className="border border-border bg-white text-black" data-testid="spe-preview-linkedin">
      <div className="flex items-start gap-2 p-2 border-b border-border">
        <_Avatar src={media} fallback={initial} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{brand.name}</div>
          <div className="text-[10px] text-muted-foreground">{followers.toLocaleString()} followers</div>
          <div className="text-[10px] text-muted-foreground">Promoted · <Linkedin className="w-3 h-3 inline" /></div>
        </div>
        {_charBadge(total, CHANNEL_LIMITS.linkedin)}
      </div>
      <div className="p-2 space-y-1">
        <div className="text-xs whitespace-pre-wrap line-clamp-6">{caption}</div>
        {tags && <div className="text-[11px] text-sky-700">{tags}</div>}
      </div>
      {media && <img src={media} alt="" className="w-full max-h-40 object-cover" />}
      <div className="p-2 border-t border-border flex items-center gap-3 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> Like</span>
        <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Comment</span>
        <span className="inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> Repost</span>
      </div>
    </div>
  );
}

function FacebookPreview({ brand, handle, caption, tags, media }) {
  const initial = (brand.name || "B").slice(0, 2).toUpperCase();
  const total = (caption?.length || 0) + (tags?.length || 0) + (tags ? 1 : 0);
  return (
    <div className="border border-border bg-white text-black" data-testid="spe-preview-facebook">
      <div className="flex items-start gap-2 p-2">
        <_Avatar src={media} fallback={initial} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{brand.name}</div>
          <div className="text-[10px] text-muted-foreground">Just now · Sponsored</div>
        </div>
        {_charBadge(total, CHANNEL_LIMITS.facebook)}
        <Facebook className="w-4 h-4 text-blue-600" />
      </div>
      <div className="px-2 pb-2 text-xs whitespace-pre-wrap line-clamp-4">{caption}</div>
      {media && <img src={media} alt="" className="w-full max-h-40 object-cover" />}
      {tags && <div className="p-2 text-[11px] text-blue-600">{tags}</div>}
    </div>
  );
}

function TwitterPreview({ brand, handle, caption, tags }) {
  const initial = (brand.name || "B").slice(0, 2).toUpperCase();
  const raw = `${caption}${tags ? "\n\n" + tags : ""}`;
  const text = raw.slice(0, 280);
  return (
    <div className="border border-border bg-black text-white p-3" data-testid="spe-preview-twitter">
      <div className="flex items-start gap-2">
        <_Avatar src={null} fallback={initial} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate flex items-center gap-1">
            {brand.name} <span className="text-muted-foreground font-normal">{handle} · now</span>
            <span className="ml-auto">{_charBadge(raw.length, CHANNEL_LIMITS.twitter)}</span>
          </div>
          <div className="text-sm whitespace-pre-wrap mt-1">{text}</div>
        </div>
        <Twitter className="w-4 h-4" />
      </div>
    </div>
  );
}

function YoutubePreview({ brand, handle, caption, tags, media }) {
  const total = (caption?.length || 0) + (tags?.length || 0) + (tags ? 1 : 0);
  return (
    <div className="border border-border" data-testid="spe-preview-youtube">
      <div className="aspect-video bg-black flex items-center justify-center">
        {media ? <img src={media} alt="" className="w-full h-full object-cover opacity-80" /> : <Youtube className="w-10 h-10 text-red-600" />}
      </div>
      <div className="p-2 space-y-1">
        <div className="flex items-center gap-1.5">
          <div className="text-sm font-semibold line-clamp-2 flex-1">{caption}</div>
          {_charBadge(total, CHANNEL_LIMITS.youtube)}
        </div>
        <div className="text-[10px] text-muted-foreground">{brand.name} · {handle}</div>
        {tags && <div className="text-[11px] text-blue-600 line-clamp-1">{tags}</div>}
      </div>
    </div>
  );
}

function WhatsAppPreview({ brand, caption, tags, media }) {
  const total = (caption?.length || 0) + (tags?.length || 0) + (tags ? 1 : 0);
  return (
    <div className="border border-border bg-emerald-50" data-testid="spe-preview-whatsapp">
      <div className="p-2 flex items-center gap-2 bg-emerald-600 text-white text-xs">
        <MessageCircle className="w-4 h-4" /> {brand.name} · WhatsApp Business
        <span className="ml-auto">{_charBadge(total, CHANNEL_LIMITS.whatsapp)}</span>
      </div>
      <div className="p-3">
        <div className="bg-white p-2 max-w-[85%] shadow-sm text-xs">
          {media && <img src={media} alt="" className="w-full max-h-32 object-cover mb-1.5" />}
          <div className="whitespace-pre-wrap line-clamp-6">{caption}</div>
          {tags && <div className="text-[11px] text-emerald-700 mt-1">{tags}</div>}
          <div className="text-[9px] text-muted-foreground text-right mt-1">Now ✓✓</div>
        </div>
      </div>
    </div>
  );
}

function GenericPreview({ channel, caption, tags }) {
  const Icon = CHANNEL_ICONS[channel] || Globe;
  return (
    <div className="border border-border p-3 space-y-2" data-testid={`spe-preview-${channel}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        <Icon className="w-4 h-4" /> {CHANNEL_LABELS[channel] || channel}
      </div>
      <div className="text-sm whitespace-pre-wrap line-clamp-4">{caption}</div>
      {tags && <div className="text-xs text-primary">{tags}</div>}
    </div>
  );
}

// ─────────── Linked tasks pane ───────────

function TasksPane({ item, tasks, onReloaded }) {
  const [addMode, setAddMode] = useState(null); // "existing" | "new"
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState("");
  const [existing, setExisting] = useState([]);
  const [existingId, setExistingId] = useState("");
  const [busy, setBusy] = useState(false);
  const wsId = item.workspace_id;

  useEffect(() => {
    if (addMode !== "existing" || !wsId) return;
    api.get(`/workspaces/${wsId}/tasks-search?limit=100`).then((r) => setExisting(r.data || [])).catch(() => setExisting([]));
  }, [addMode, wsId]);

  const add = async () => {
    setBusy(true);
    try {
      const payload = addMode === "new"
        ? { mode: "new", title, project_id: projectId || null }
        : { mode: "existing", task_id: existingId };
      await api.post(`/content-items/${item.id}/linked-tasks`, payload);
      toast.success("Task linked");
      setAddMode(null); setTitle(""); setExistingId("");
      await onReloaded();
    } catch (e) { toast.error(apiErrorMessage(e, "Link failed")); }
    finally { setBusy(false); }
  };

  const remove = async (tid) => {
    if (!window.confirm("Remove this task from the post? (deletes if not done)")) return;
    try {
      await api.delete(`/content-items/${item.id}/linked-tasks/${tid}?delete_task=true`);
      toast.success("Unlinked");
      await onReloaded();
    } catch (e) { toast.error(apiErrorMessage(e, "Unlink failed")); }
  };

  return (
    <div className="space-y-3" data-testid="spe-tasks">
      <div className="border border-border">
        {!tasks ? (
          <div className="p-3 text-xs text-muted-foreground">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="p-3 text-xs italic text-muted-foreground">No linked tasks yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {tasks.map((t) => (
              <div key={t.id} className="px-3 py-2 flex items-center gap-2 text-xs" data-testid={`spe-task-${t.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${t.status === "done" ? "bg-emerald-600" : t.status === "in_progress" ? "bg-amber-500" : "bg-muted-foreground/40"}`} />
                {/* R125 · Assignee + due date IN FRONT of task title */}
                {t.assignee_name && (
                  <span className="text-muted-foreground shrink-0 max-w-[120px] truncate" title={t.assignee_name}
                        data-testid={`spe-task-assignee-${t.id}`}>{t.assignee_name}</span>
                )}
                {t.due_date && (
                  <span className={`font-mono text-[10px] shrink-0 ${
                    t.due_date < new Date().toISOString().slice(0, 10) && t.status !== "done"
                      ? "text-rose-600 font-semibold"
                      : "text-muted-foreground"
                  }`}
                        title={`Due ${t.due_date}`}
                        data-testid={`spe-task-due-${t.id}`}>{t.due_date.slice(5)}</span>
                )}
                <span className="flex-1 truncate">{t.title}</span>
                {t.assignee_hint && <Badge variant="outline" className="rounded-none text-[9px]">{t.assignee_hint}</Badge>}
                <span className="text-muted-foreground">{t.status}</span>
                <Button size="sm" variant="ghost" className="rounded-none h-6 w-6 p-0 text-destructive"
                        onClick={() => remove(t.id)} data-testid={`spe-task-remove-${t.id}`}>
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {addMode === null && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => setAddMode("new")} data-testid="spe-task-new">
            <Plus className="w-3 h-3 mr-1" /> New task
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => setAddMode("existing")} data-testid="spe-task-link-existing">
            <ListChecks className="w-3 h-3 mr-1" /> Link existing
          </Button>
        </div>
      )}
      {addMode === "new" && (
        <div className="border border-border p-3 space-y-2" data-testid="spe-task-new-form">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" className="h-9 rounded-none" data-testid="spe-task-new-title" />
          <Input value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="Project ID (blank uses post's project)" className="h-9 rounded-none" data-testid="spe-task-new-project" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setAddMode(null)}>Cancel</Button>
            <Button size="sm" className="rounded-none" onClick={add} disabled={busy || !title.trim()} data-testid="spe-task-new-save">
              {busy ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
      )}
      {addMode === "existing" && (
        <div className="border border-border p-3 space-y-2" data-testid="spe-task-link-form">
          <Select value={existingId} onValueChange={setExistingId}>
            <SelectTrigger className="h-9 rounded-none" data-testid="spe-task-existing-select"><SelectValue placeholder="Pick task…" /></SelectTrigger>
            <SelectContent>
              {existing.slice(0, 50).map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setAddMode(null)}>Cancel</Button>
            <Button size="sm" className="rounded-none" onClick={add} disabled={busy || !existingId} data-testid="spe-task-link-save">
              {busy ? "Linking…" : "Link"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────── Hashtags pane ───────────

function HashtagsPane({ itemId, form, set }) {
  const [suggested, setSuggested] = useState(null);
  const [source, setSource] = useState("static");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.post(`/content-items/${itemId}/hashtags`, { use_ai: false, max: 15 })
      .then((r) => {
        setSuggested(r.data.hashtags || []);
        setSource(r.data.source || "static");
      })
      .catch(() => setSuggested([]))
      .finally(() => setLoading(false));
  }, [itemId]);

  const runAi = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/content-items/${itemId}/hashtags`, { use_ai: true, max: 15 });
      setSuggested(r.data.hashtags);
      setSource(r.data.source);
      toast.success(`Refreshed via ${r.data.source}`);
    } catch (e) { toast.error(apiErrorMessage(e, "AI hashtags failed")); }
    finally { setLoading(false); }
  };

  const toggle = (tag) => {
    if (form.hashtags.includes(tag)) set("hashtags", form.hashtags.filter((t) => t !== tag));
    else set("hashtags", [...form.hashtags, tag]);
  };
  const [manual, setManual] = useState("");
  const addManual = () => {
    let t = manual.trim();
    if (!t) return;
    if (!t.startsWith("#")) t = "#" + t;
    t = t.replace(/\s+/g, "");
    if (!form.hashtags.includes(t)) set("hashtags", [...form.hashtags, t]);
    setManual("");
  };

  return (
    <div className="space-y-3" data-testid="spe-hashtags">
      <div className="flex items-center gap-2">
        <Label className="label-eyebrow">Selected · {form.hashtags.length}</Label>
        <Button size="sm" variant="outline" className="rounded-none ml-auto" onClick={runAi} disabled={loading} data-testid="spe-hashtags-ai">
          <Wand2 className="w-3 h-3 mr-1" /> {loading ? "…" : "Better hashtags (AI)"}
        </Button>
      </div>
      <div className="border border-border p-2 min-h-[60px] flex flex-wrap gap-1">
        {form.hashtags.map((t) => (
          <button key={t} type="button" onClick={() => toggle(t)}
                  className="text-xs px-1.5 py-0.5 bg-primary text-primary-foreground"
                  data-testid={`spe-hashtag-selected-${t.replace(/#/g, "")}`}>
            {t} ×
          </button>
        ))}
        {form.hashtags.length === 0 && <span className="text-xs italic text-muted-foreground">Pick from suggestions or type your own</span>}
      </div>
      <div className="flex gap-1">
        <Input value={manual} onChange={(e) => setManual(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManual(); } }}
               placeholder="type a hashtag & Enter" className="h-8 rounded-none text-xs" data-testid="spe-hashtag-manual" />
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={addManual}>Add</Button>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <Label className="label-eyebrow">Suggestions ({source})</Label>
          {loading && <span className="text-[10px] text-muted-foreground italic">Loading…</span>}
        </div>
        <div className="flex flex-wrap gap-1">
          {(suggested || []).map((t) => {
            const on = form.hashtags.includes(t);
            return (
              <button key={t} type="button" onClick={() => toggle(t)}
                      className={`text-xs px-1.5 py-0.5 border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                      data-testid={`spe-hashtag-suggested-${t.replace(/#/g, "")}`}>
                <Hash className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{t.replace(/^#/, "")}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────── AI publish-time pane ───────────

function AiTimePane({ itemId, form, set }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  const suggest = async (useAi) => {
    setLoading(true);
    try {
      const r = await api.post(`/content-items/${itemId}/suggest-time`, { use_ai: useAi });
      setState(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Suggest failed")); }
    finally { setLoading(false); }
  };

  const apply = (t) => {
    set("planned_time", t);
    toast.success(`Applied ${t}`);
  };

  return (
    <div className="space-y-3" data-testid="spe-ai-time">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => suggest(false)} disabled={loading}>
          <Clock className="w-3 h-3 mr-1" /> Rules-based (fast)
        </Button>
        <Button size="sm" className="rounded-none" onClick={() => suggest(true)} disabled={loading} data-testid="spe-ai-time-suggest">
          <Sparkles className="w-3 h-3 mr-1" /> {loading ? "Thinking…" : "Suggest best time (AI)"}
        </Button>
      </div>
      <div className="border border-border p-3 text-xs">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Current planned time</div>
        <div className="text-lg font-mono text-primary">{form.planned_time || "—"}</div>
      </div>
      {state && (
        <div className="border border-border p-3 space-y-2" data-testid="spe-ai-time-result">
          <div className="flex items-center gap-2">
            <span className="chip chip-approved text-[10px]">Top pick · {state.source}</span>
            <span className="text-2xl font-mono">{state.planned_time}</span>
            <Button size="sm" className="rounded-none ml-auto" onClick={() => apply(state.planned_time)} data-testid="spe-ai-time-apply">
              Apply
            </Button>
          </div>
          <div className="text-[11px] text-muted-foreground italic">{state.reason}</div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">All candidates</div>
            <div className="space-y-1">
              {(state.candidates || []).map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs" data-testid={`spe-ai-time-cand-${i}`}>
                  <span className="w-14 font-mono">{c.time}</span>
                  <span className="chip text-[9px]">{c.source}</span>
                  <div className="flex-1 bg-muted h-1.5">
                    <div className="h-full bg-primary" style={{ width: `${Math.min(100, c.score * 100)}%` }} />
                  </div>
                  <button className="text-primary text-[10px] hover:underline" onClick={() => apply(c.time)}>
                    Apply
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



// ─── Timeline Pane (Iter 208) ─────────────────────────────────────
function TimelinePane({ wsId, itemId }) {
  const [events, setEvents] = useState(null);
  const [posting, setPosting] = useState(false);
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [scope, setScope] = useState("all"); // all | internal | client
  // @-mention popover state.
  const [members, setMembers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // string | null
  const [mentionAnchor, setMentionAnchor] = useState(0);  // caret index of "@"
  const [mentionIdx, setMentionIdx] = useState(0);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const qs = new URLSearchParams({ limit: "200" });
      if (scope !== "all") qs.set("visibility", scope);
      const { data } = await api.get(
        `/workspaces/${wsId}/content-items/${itemId}/timeline?${qs.toString()}`);
      setEvents(data.events || []);
    } catch { setEvents([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, itemId, scope]);
  // Load mention directory once — used by the @-picker below.
  useEffect(() => {
    api.get(`/workspaces/${wsId}/mention-directory`)
      .then((r) => setMembers(r.data || []))
      .catch(() => setMembers([]));
  }, [wsId]);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await api.post(
        `/workspaces/${wsId}/content-items/${itemId}/comments`,
        { body: text.trim(), visibility });
      setText("");
      toast.success(visibility === "client"
        ? "Client-visible comment posted"
        : "Comment posted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Comment failed")); }
    finally { setPosting(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(
        `/workspaces/${wsId}/content-items/${itemId}/comments/${id}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // @-mention autocomplete driver. Watches the textarea for a lone "@"
  // followed by a partial name/email, and shows a filtered member list.
  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart ?? val.length;
    // Walk backwards from the caret looking for an "@" that starts a mention
    // token. Abort if we hit whitespace, newline, or the token gets long.
    let i = caret - 1;
    let token = "";
    while (i >= 0 && val[i] !== "@") {
      const ch = val[i];
      if (/\s/.test(ch) || i < caret - 40) { i = -2; break; }
      token = ch + token;
      i--;
    }
    if (i >= 0 && val[i] === "@" && (i === 0 || /\s/.test(val[i - 1]) || val[i - 1] === "(" )) {
      setMentionAnchor(i);
      setMentionQuery(token);
      setMentionIdx(0);
    } else {
      setMentionQuery(null);
    }
  };

  const mentionMatches = (() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.trim().toLowerCase();
    return members
      .filter((m) => !q
        || (m.name || "").toLowerCase().includes(q)
        || (m.email || "").toLowerCase().includes(q))
      .slice(0, 6);
  })();

  const insertMention = (m) => {
    const tag = `@[${m.name || m.email}](${m.id})`;
    const before = text.slice(0, mentionAnchor);
    const after = text.slice(mentionAnchor + 1 + (mentionQuery?.length || 0));
    const next = `${before}${tag} ${after}`;
    setText(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      const pos = (before + tag + " ").length;
      inputRef.current?.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (mentionQuery !== null && mentionMatches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx((i) => Math.min(i + 1, mentionMatches.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setMentionIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(mentionMatches[mentionIdx]);
        return;
      }
      if (e.key === "Escape") { setMentionQuery(null); return; }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); post(); }
  };

  // Render a comment body with @[Name](uid) replaced by a highlighted chip.
  const renderBody = (body) => {
    if (!body) return null;
    const parts = [];
    const re = /@\[([^\]]{1,80})\]\(([0-9a-fA-F-]{20,})\)/g;
    let last = 0; let m;
    let key = 0;
    while ((m = re.exec(body)) !== null) {
      if (m.index > last) parts.push(<span key={key++}>{body.slice(last, m.index)}</span>);
      parts.push(<span key={key++} className="bg-primary/15 text-primary px-1 rounded font-medium">@{m[1]}</span>);
      last = m.index + m[0].length;
    }
    if (last < body.length) parts.push(<span key={key++}>{body.slice(last)}</span>);
    return <span className="whitespace-pre-wrap">{parts}</span>;
  };

  // Human-readable label for a field-change activity entry.
  const renderActivity = (e) => {
    const md = e.metadata || {};
    if (e.action === "content_item.field_updated" && md.field) {
      return (
        <div className="mt-0.5 text-muted-foreground">
          <span className="font-medium capitalize text-foreground/90">{String(md.field).replace(/_/g, " ")}</span>
          {": "}
          <span className="line-through text-muted-foreground/70">{md.old || "—"}</span>
          {" → "}
          <span className="text-foreground">{md.new || "—"}</span>
        </div>
      );
    }
    return (
      <div className="mt-0.5 text-muted-foreground">
        {e.target_label ? `on ${e.target_type} · ${e.target_label}` : e.target_type}
        {md.body_preview ? ` — “${md.body_preview}”` : ""}
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="spe-timeline">
      <div className="border border-border p-3 bg-muted/20 relative">
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
            Add a comment
          </div>
          <div className="flex items-center gap-1 text-[10px]">
            <label className="inline-flex items-center gap-1 cursor-pointer" title="Team-only comment (default)">
              <input type="radio" name="cc-vis" checked={visibility === "internal"}
                      onChange={() => setVisibility("internal")}
                      data-testid="spe-comment-visibility-internal" />
              Internal
            </label>
            <label className="inline-flex items-center gap-1 cursor-pointer text-amber-700" title="Also visible on the client portal">
              <input type="radio" name="cc-vis" checked={visibility === "client"}
                      onChange={() => setVisibility("client")}
                      data-testid="spe-comment-visibility-client" />
              Client-visible
            </label>
          </div>
        </div>
        <textarea rows={3} value={text}
                   ref={inputRef}
                   onChange={onTextChange}
                   onKeyDown={onKeyDown}
                   className="w-full text-sm border border-border bg-background p-2"
                   placeholder="Add a comment… Use @ to tag a teammate."
                   data-testid="spe-timeline-comment-input" />
        {mentionQuery !== null && mentionMatches.length > 0 && (
          <div className="absolute z-30 mt-1 max-h-56 w-64 overflow-y-auto border border-border bg-background shadow-lg"
                data-testid="spe-mention-popover">
            {mentionMatches.map((m, i) => (
              <button key={m.id} type="button"
                       onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                       onMouseEnter={() => setMentionIdx(i)}
                       className={`w-full text-left px-2 py-1.5 text-xs flex items-center gap-2 ${i === mentionIdx ? "bg-muted/60" : "hover:bg-muted/40"}`}
                       data-testid={`spe-mention-opt-${m.id}`}>
                <span className="w-5 h-5 bg-primary/10 text-primary text-[10px] rounded-full flex items-center justify-center shrink-0">
                  {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                </span>
                <span className="truncate flex-1">{m.name || m.email}</span>
                {m.name && m.email && <span className="text-[10px] text-muted-foreground truncate">{m.email}</span>}
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-between items-center mt-1">
          <div className="text-[10px] text-muted-foreground">Cmd/Ctrl + Enter to post</div>
          <Button size="sm" className="rounded-none" onClick={post}
                   disabled={posting || !text.trim()}
                   data-testid="spe-timeline-post">
            {posting ? "Posting…" : "Post comment"}
          </Button>
        </div>
      </div>

      {/* Visibility filter chips */}
      <div className="flex items-center gap-2 text-[10px]" data-testid="spe-timeline-scope">
        <span className="uppercase tracking-widest text-muted-foreground">Show</span>
        {["all", "internal", "client"].map((s) => (
          <button key={s} type="button"
                   onClick={() => setScope(s)}
                   className={`px-2 py-0.5 border ${scope === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                   data-testid={`spe-timeline-scope-${s}`}>
            {s === "all" ? "All" : s === "internal" ? "Internal" : "Client-visible"}
          </button>
        ))}
      </div>

      {events === null ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-xs italic text-muted-foreground text-center py-6">
          No activity or comments yet.
        </div>
      ) : (
        <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1"
              data-testid="spe-timeline-events">
          {events.map((e) => (
            <div key={e.id}
                  className={`border-l-2 pl-3 py-1 text-xs ${
                    e.kind === "comment"
                      ? (e.visibility === "client"
                          ? "border-amber-500 bg-amber-500/5"
                          : "border-primary/50 bg-primary/5")
                      : "border-muted-foreground/30"}`}
                  data-testid={`spe-timeline-event-${e.id}`}>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <span className="font-semibold text-foreground">{e.actor_name || "—"}</span>
                <span>·</span>
                <span>{String(e.created_at || "").slice(0, 16).replace("T"," ")}</span>
                {e.kind === "activity" && e.action && (
                  <>
                    <span>·</span>
                    <span className="uppercase tracking-widest text-[9px]">{e.action.replace("content_item.", "")}</span>
                  </>
                )}
                {e.kind === "comment" && e.visibility === "client" && (
                  <span className="uppercase tracking-widest text-[9px] px-1 py-[1px] bg-amber-500/15 text-amber-700 rounded"
                         data-testid={`spe-timeline-badge-client-${e.id}`}>Client-visible</span>
                )}
                {e.kind === "comment" && e.visibility !== "client" && (
                  <span className="uppercase tracking-widest text-[9px] px-1 py-[1px] bg-primary/10 text-primary rounded"
                         data-testid={`spe-timeline-badge-internal-${e.id}`}>Internal</span>
                )}
                {e.edited_at && <span className="italic">· edited</span>}
                {e.kind === "comment" && (
                  <button type="button" onClick={() => del(e.id)}
                           className="ml-auto text-red-600 hover:underline text-[10px]"
                           data-testid={`spe-timeline-delete-${e.id}`}>
                    delete
                  </button>
                )}
              </div>
              {e.kind === "comment" ? (
                <div className="mt-0.5">{renderBody(e.body)}</div>
              ) : (
                renderActivity(e)
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
